import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Award, Lock, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface Milestone {
  id: string;
  name: string;
  description?: string;
  targetDays?: number;
  achievedAt?: string;
  isAchieved: boolean;
  isNext?: boolean;
}

interface MilestoneTimelineWidgetProps {
  config: {
    title?: string;
    showDates?: boolean;
    maxDisplay?: number;
  };
  data?: {
    milestones?: Milestone[];
    currentDay?: number;
  };
}

export function MilestoneTimelineWidget({ config, data }: MilestoneTimelineWidgetProps) {
  const title = config.title || 'Milestones';
  const milestones = data?.milestones || [];
  const currentDay = data?.currentDay || 0;
  const maxDisplay = config.maxDisplay || 6;

  // Find the next milestone and display around it
  const nextIndex = milestones.findIndex(m => !m.isAchieved);
  const startIndex = Math.max(0, nextIndex - 1);
  const displayMilestones = milestones.slice(startIndex, startIndex + maxDisplay);

  if (milestones.length === 0) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Award className="h-4 w-4 text-primary" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-32">
          <p className="text-sm text-muted-foreground">No milestones yet</p>
        </CardContent>
      </Card>
    );
  }

  const achievedCount = milestones.filter(m => m.isAchieved).length;

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Award className="h-4 w-4 text-primary" />
            {title}
          </CardTitle>
          <span className="text-xs text-muted-foreground">
            Day {currentDay}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-muted" />
          
          <div className="space-y-4">
            {displayMilestones.map((milestone, index) => {
              const isNext = !milestone.isAchieved && 
                (index === 0 || displayMilestones[index - 1].isAchieved);

              return (
                <div key={milestone.id} className="relative flex gap-3 pl-1">
                  {/* Icon */}
                  <div className={cn(
                    "relative z-10 flex items-center justify-center w-5 h-5 rounded-full",
                    milestone.isAchieved 
                      ? "bg-green-100 dark:bg-green-900/30" 
                      : isNext
                        ? "bg-primary/20 ring-2 ring-primary"
                        : "bg-muted"
                  )}>
                    {milestone.isAchieved ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    ) : isNext ? (
                      <Award className="h-3 w-3 text-primary" />
                    ) : (
                      <Lock className="h-3 w-3 text-muted-foreground" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={cn(
                        "text-sm font-medium",
                        !milestone.isAchieved && !isNext && "text-muted-foreground"
                      )}>
                        {milestone.name}
                      </p>
                      {milestone.targetDays && (
                        <span className="text-xs text-muted-foreground">
                          Day {milestone.targetDays}
                        </span>
                      )}
                    </div>
                    {milestone.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {milestone.description}
                      </p>
                    )}
                    {config.showDates && milestone.achievedAt && (
                      <p className="text-xs text-green-600 mt-0.5">
                        Achieved {format(new Date(milestone.achievedAt), 'MMM d, yyyy')}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-4 pt-2 border-t">
          {achievedCount} of {milestones.length} milestones achieved
        </p>
      </CardContent>
    </Card>
  );
}
